import React, { useState } from "react";
import { api, authHeader, errMessage, downloadAuthed, fileUrl } from "../../api";
import { fmtWhen, kindLabel, toLocalInput, fromLocalInput, relative } from "../../lib/dates";

const BLANK = {
  title: "",
  kind: "circle",
  starts_at: "",
  ends_at: "",
  when_text: "",
  venue: "",
  address: "",
  address_hidden: true,
  is_tbc: false,
  blurb: "",
  capacity: "",
  published: true,
  rsvp_open: true,
};

function GatheringForm({ initial, onCancel, onSaved }) {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState(
    initial
      ? {
          ...BLANK,
          ...initial,
          starts_at: toLocalInput(initial.starts_at),
          when_text: initial.when_text || "",
          ends_at: toLocalInput(initial.ends_at),
          capacity: initial.capacity ?? "",
        }
      : BLANK,
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((s) => ({ ...s, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.title.trim()) return setErr("It needs a title.");
    if (!f.starts_at) return setErr("It needs a date and a start time.");

    const payload = {
      title: f.title.trim(),
      kind: f.kind,
      starts_at: fromLocalInput(f.starts_at),
      ends_at: f.ends_at ? fromLocalInput(f.ends_at) : null,
      when_text: f.when_text.trim(),
      venue: f.venue.trim(),
      address: f.address.trim(),
      address_hidden: Boolean(f.address_hidden),
      is_tbc: Boolean(f.is_tbc),
      blurb: f.blurb.trim(),
      capacity:
        f.capacity === "" || f.capacity === null ? null : Number(f.capacity),
      published: Boolean(f.published),
      rsvp_open: Boolean(f.rsvp_open),
    };

    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/gatherings/${initial.id}`, payload, {
          headers: authHeader(),
        });
      } else {
        await api.post("/admin/gatherings", payload, { headers: authHeader() });
      }
      onSaved();
    } catch (e2) {
      setErr(errMessage(e2, "That didn't save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={save} data-testid="admin-gathering-form">
      <fieldset>
        <legend>{editing ? "Edit this gathering" : "A new gathering"}</legend>
        <div className="field">
          <label htmlFor="g-title">Title</label>
          <input
            id="g-title"
            value={f.title}
            onChange={set("title")}
            placeholder="Writers' circle — first meeting"
            data-testid="admin-gathering-title-input"
          />
        </div>
        <div className="row3">
          <div className="field">
            <label htmlFor="g-kind">What kind</label>
            <select
              id="g-kind"
              value={f.kind}
              onChange={set("kind")}
              data-testid="admin-gathering-kind-select"
            >
              <option value="circle">Circle</option>
              <option value="showing">Showing</option>
              <option value="special">Special gathering</option>
              <option value="one-off">One-off</option>
              <option value="neighbourhoods">Neighbourhoods</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="g-start">Starts</label>
            <input
              id="g-start"
              type="datetime-local"
              value={f.starts_at}
              onChange={set("starts_at")}
              data-testid="admin-gathering-start-input"
            />
            <span className="hint">Calgary time. Type it as people will read it.</span>
          </div>
          <div className="field">
            <label htmlFor="g-end">
              Ends <span className="opt">&mdash; optional</span>
            </label>
            <input
              id="g-end"
              type="datetime-local"
              value={f.ends_at}
              onChange={set("ends_at")}
              data-testid="admin-gathering-end-input"
            />
            <span className="hint">Left blank, we assume two hours.</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="g-whentext">
            Say it in words instead{" "}
            <span className="opt">&mdash; optional</span>
          </label>
          <input
            id="g-whentext"
            value={f.when_text}
            onChange={set("when_text")}
            placeholder="Mid November"
            data-testid="admin-gathering-whentext-input"
          />
          <span className="hint">
            For a date you haven&rsquo;t settled. Visitors see this instead of the
            date above, with &ldquo;date to be confirmed&rdquo; beneath it. The date
            above still keeps the list in order, so put your best guess there.
          </span>
        </div>
      </fieldset>

      <fieldset>
        <legend>Where</legend>
        <div className="row3">
          <div className="field">
            <label htmlFor="g-venue">Venue or neighbourhood</label>
            <input
              id="g-venue"
              value={f.venue}
              onChange={set("venue")}
              placeholder="Bridgeland"
              data-testid="admin-gathering-venue-input"
            />
          </div>
          <div className="field">
            <label htmlFor="g-address">
              Full address <span className="opt">&mdash; optional</span>
            </label>
            <input
              id="g-address"
              value={f.address}
              onChange={set("address")}
              data-testid="admin-gathering-address-input"
            />
          </div>
          <div className="field">
            <label htmlFor="g-capacity">
              Seats <span className="opt">&mdash; blank for no limit</span>
            </label>
            <input
              id="g-capacity"
              type="number"
              min={0}
              value={f.capacity}
              onChange={set("capacity")}
              data-testid="admin-gathering-capacity-input"
            />
            <span className="hint">Seats are a kindness, not a scarcity trick.</span>
          </div>
        </div>
        <div className="check">
          <input
            id="g-hidden"
            type="checkbox"
            checked={Boolean(f.address_hidden)}
            onChange={set("address_hidden")}
            data-testid="admin-gathering-address-hidden-checkbox"
          />
          <div>
            <label htmlFor="g-hidden">Keep the address off the public page.</label>
            <span className="hint">
              The site will say &ldquo;address sent when you write&rdquo; instead.
              Nothing you typed above is shown to anyone.
            </span>
          </div>
        </div>
        <div className="check">
          <input
            id="g-tbc"
            type="checkbox"
            checked={Boolean(f.is_tbc)}
            onChange={set("is_tbc")}
            data-testid="admin-gathering-tbc-checkbox"
          />
          <div>
            <label htmlFor="g-tbc">The location isn&rsquo;t settled yet.</label>
            <span className="hint">Shows as &ldquo;to be confirmed&rdquo;.</span>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>What to say about it</legend>
        <div className="field">
          <label htmlFor="g-blurb">A line or two</label>
          <textarea
            id="g-blurb"
            rows={3}
            value={f.blurb}
            onChange={set("blurb")}
            placeholder="Bring something unfinished. Three to six of us, two hours, a table."
            data-testid="admin-gathering-blurb-textarea"
          />
        </div>
        <div className="check">
          <input
            id="g-pub"
            type="checkbox"
            checked={Boolean(f.published)}
            onChange={set("published")}
            data-testid="admin-gathering-published-checkbox"
          />
          <div>
            <label htmlFor="g-pub">Show this on the public site.</label>
            <span className="hint">
              Publishing makes it visible to everyone. You can unpublish later and
              nothing is lost.
            </span>
          </div>
        </div>
        <div className="check">
          <input
            id="g-rsvp"
            type="checkbox"
            checked={Boolean(f.rsvp_open)}
            onChange={set("rsvp_open")}
            data-testid="admin-gathering-rsvp-open-checkbox"
          />
          <div>
            <label htmlFor="g-rsvp">Let people RSVP.</label>
            <span className="hint">
              Turn this off and the listing still shows, but without the form.
            </span>
          </div>
        </div>
      </fieldset>

      {err ? (
        <div className="form-error" data-testid="admin-gathering-form-error">
          {err}
        </div>
      ) : null}

      <div className="actions">
        <button
          type="submit"
          className="send"
          disabled={saving}
          data-testid="admin-gathering-save-button"
        >
          {saving ? "Saving\u2026" : editing ? "Save changes" : "Create it"}
        </button>
        <button
          type="button"
          className="linkish"
          onClick={onCancel}
          data-testid="admin-gathering-cancel-button"
        >
          Never mind
        </button>
      </div>
    </form>
  );
}

function RsvpList({ gathering, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    let alive = true;
    api
      .get(`/admin/gatherings/${gathering.id}/rsvps`, { headers: authHeader() })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(errMessage(e)));
    return () => {
      alive = false;
    };
  }, [gathering.id]);

  const rows = data?.rsvps || [];
  const confirmed = rows.filter((r) => r.status === "confirmed");
  const waiting = rows.filter((r) => r.status === "waitlist");
  const gone = rows.filter((r) => r.status === "cancelled");

  return (
    <div className="adm-form" data-testid="admin-rsvp-list">
      <header className="admin-sec" style={{ marginBottom: "1.2rem" }}>
        <h2 style={{ fontSize: "1.5rem" }}>Who&rsquo;s coming</h2>
        <p className="admin-help" style={{ margin: ".4rem 0 0" }}>
          {gathering.title} &mdash; {fmtWhen(gathering.starts_at, gathering.ends_at)}
        </p>
      </header>

      {err ? <div className="form-error">{err}</div> : null}
      {!data && !err ? <p className="admin-help">Looking&hellip;</p> : null}

      {data ? (
        <>
          <p className="admin-help">
            {confirmed.length} coming
            {waiting.length ? `, ${waiting.length} on the waitlist` : ""}
            {gone.length ? `, ${gone.length} cancelled` : ""}.
          </p>

          {rows.length === 0 ? (
            <div className="empty" data-testid="admin-rsvp-empty">
              <p>Nobody yet. It&rsquo;s early.</p>
            </div>
          ) : (
            <>
              <table className="tbl" data-testid="admin-rsvps-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th className="right">Party</th>
                    <th>Status</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <span className="t">{r.name}</span>
                      </td>
                      <td>
                        <a href={`mailto:${r.email}`}>{r.email}</a>
                      </td>
                      <td className="num right">{r.seats}</td>
                      <td>
                        <span
                          className={`chip ${
                            r.status === "confirmed"
                              ? "chip--on"
                              : r.status === "waitlist"
                              ? "chip--new"
                              : "chip--off"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td>{r.note || "\u2014"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="stack">
                {rows.map((r) => (
                  <div className="rowcard" key={r.id}>
                    <div className="rt">{r.name}</div>
                    <dl>
                      <dt>Email</dt>
                      <dd>
                        <a href={`mailto:${r.email}`}>{r.email}</a>
                      </dd>
                      <dt>Party</dt>
                      <dd>{r.seats}</dd>
                      <dt>Status</dt>
                      <dd>{r.status}</dd>
                      {r.note ? (
                        <>
                          <dt>Note</dt>
                          <dd>{r.note}</dd>
                        </>
                      ) : null}
                    </dl>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="actions">
            <button
              type="button"
              className="act"
              onClick={() =>
                downloadAuthed(
                  `/admin/export/rsvps.csv?gathering_id=${gathering.id}`,
                  "schema-rsvps.csv",
                )
              }
              data-testid="admin-rsvps-export-button"
            >
              Export this list (CSV)
            </button>
            <button type="button" className="linkish" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function GatheringsPanel({ rows, reload, loading }) {
  const [mode, setMode] = useState(null); // null | 'new' | {edit} | {rsvps}
  const [busy, setBusy] = useState("");

  const remove = async (g) => {
    const ok = window.confirm(
      `Delete "${g.title}"?\n\nThis also removes its RSVP list. This can't be undone.`,
    );
    if (!ok) return;
    setBusy(g.id);
    try {
      await api.delete(`/admin/gatherings/${g.id}`, { headers: authHeader() });
      reload();
    } catch (e) {
      window.alert(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const togglePublish = async (g) => {
    setBusy(g.id);
    try {
      await api.put(
        `/admin/gatherings/${g.id}`,
        {
          title: g.title,
          kind: g.kind,
          starts_at: g.starts_at,
          ends_at: g.ends_at,
          venue: g.venue || "",
          address: g.address || "",
          address_hidden: g.address_hidden,
          is_tbc: g.is_tbc,
          blurb: g.blurb || "",
          capacity: g.capacity,
          published: !g.published,
          rsvp_open: g.rsvp_open,
        },
        { headers: authHeader() },
      );
      reload();
    } catch (e) {
      window.alert(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (mode === "new") {
    return (
      <GatheringForm
        onCancel={() => setMode(null)}
        onSaved={() => {
          setMode(null);
          reload();
        }}
      />
    );
  }
  if (mode?.edit) {
    return (
      <GatheringForm
        initial={mode.edit}
        onCancel={() => setMode(null)}
        onSaved={() => {
          setMode(null);
          reload();
        }}
      />
    );
  }
  if (mode?.rsvps) {
    return <RsvpList gathering={mode.rsvps} onClose={() => setMode(null)} />;
  }

  return (
    <section className="admin-sec" data-testid="admin-gatherings-panel">
      <header>
        <h2>Gatherings</h2>
        <button
          type="button"
          className="act"
          onClick={() => setMode("new")}
          data-testid="admin-gatherings-create-button"
        >
          Make a gathering &rarr;
        </button>
      </header>
      <p className="admin-help">
        This is the calendar the public site reads from. Add a date here and it
        appears on <em>Gatherings</em> straight away, with its own RSVP form and
        its own &ldquo;add to calendar&rdquo; file. Nothing else to do.
      </p>

      {loading ? (
        <p className="admin-help">Looking&hellip;</p>
      ) : rows.length === 0 ? (
        <div className="empty" data-testid="admin-gatherings-empty">
          <h3 style={{ marginBottom: ".8rem" }}>No gatherings yet.</h3>
          <p>That&rsquo;s fine. Create one when you&rsquo;re ready.</p>
          <p style={{ marginTop: "1.4rem" }}>
            <button
              type="button"
              className="linkish"
              onClick={() => setMode("new")}
              data-testid="admin-gatherings-empty-create-button"
            >
              Make a gathering &rarr;
            </button>
          </p>
        </div>
      ) : (
        <>
          <table className="tbl" data-testid="admin-gatherings-table">
            <thead>
              <tr>
                <th>What</th>
                <th>When</th>
                <th className="right">Coming</th>
                <th>Showing?</th>
                <th className="right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id} data-testid={`admin-gathering-row-${g.id}`}>
                  <td>
                    <span className="t">{g.title}</span>
                    <br />
                    <span className="chip">{kindLabel(g.kind)}</span>{" "}
                    {g.venue ? (
                      <span style={{ color: "var(--ink-mute)", fontSize: ".88rem" }}>
                        {g.venue}
                      </span>
                    ) : null}
                  </td>
                  <td className="num">
                    {fmtWhen(g.starts_at, g.ends_at)}
                    <br />
                    <span style={{ color: "var(--ink-mute)", fontSize: ".85rem" }}>
                      {g.is_past ? "already happened" : relative(g.starts_at)}
                    </span>
                  </td>
                  <td className="num right">
                    {g.rsvp_confirmed}
                    {g.capacity ? ` / ${g.capacity}` : ""}
                    {g.rsvp_waitlist ? (
                      <>
                        <br />
                        <span style={{ color: "var(--red)", fontSize: ".82rem" }}>
                          +{g.rsvp_waitlist} waiting
                        </span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    <span className={`chip ${g.published ? "chip--on" : "chip--off"}`}>
                      {g.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="right">
                    <div className="rowacts">
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => setMode({ rsvps: g })}
                        data-testid={`admin-gathering-rsvps-button-${g.id}`}
                      >
                        Who&rsquo;s coming
                      </button>
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => setMode({ edit: g })}
                        data-testid={`admin-gathering-edit-button-${g.id}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="linkish"
                        disabled={busy === g.id}
                        onClick={() => togglePublish(g)}
                        data-testid={`admin-gathering-publish-button-${g.id}`}
                      >
                        {g.published ? "Hide" : "Publish"}
                      </button>
                      <a
                        className="linkish"
                        href={fileUrl(`/gatherings/${g.id}/calendar.ics`)}
                      >
                        .ics
                      </a>
                      <button
                        type="button"
                        className="linkish"
                        disabled={busy === g.id}
                        onClick={() => remove(g)}
                        data-testid={`admin-gathering-delete-button-${g.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="stack">
            {rows.map((g) => (
              <div className="rowcard" key={g.id}>
                <div className="rt">{g.title}</div>
                <span className="chip">{kindLabel(g.kind)}</span>{" "}
                <span className={`chip ${g.published ? "chip--on" : "chip--off"}`}>
                  {g.published ? "Published" : "Draft"}
                </span>
                <dl>
                  <dt>When</dt>
                  <dd>{fmtWhen(g.starts_at, g.ends_at)}</dd>
                  <dt>Coming</dt>
                  <dd>
                    {g.rsvp_confirmed}
                    {g.capacity ? ` / ${g.capacity}` : ""}
                    {g.rsvp_waitlist ? ` (+${g.rsvp_waitlist} waiting)` : ""}
                  </dd>
                </dl>
                <div className="rowacts">
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setMode({ rsvps: g })}
                  >
                    Who&rsquo;s coming
                  </button>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setMode({ edit: g })}
                  >
                    Edit
                  </button>
                  <button type="button" className="linkish" onClick={() => togglePublish(g)}>
                    {g.published ? "Hide" : "Publish"}
                  </button>
                  <button type="button" className="linkish" onClick={() => remove(g)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="actions">
            <button
              type="button"
              className="act"
              onClick={() => downloadAuthed("/admin/export/rsvps.csv", "schema-rsvps.csv")}
              data-testid="admin-all-rsvps-export-button"
            >
              Export every RSVP (CSV)
            </button>
          </div>
        </>
      )}
    </section>
  );
}
